import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, addDoc, collection, query, where, getDocs, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { 
  Loader2, Mail, Lock, User, AlertCircle, ArrowRight, 
  RefreshCw, CheckCircle2, ShieldCheck, GraduationCap 
} from 'lucide-react';
import PasswordInput from '../components/auth/PasswordInput';
import { useToast } from '../context/ToastContext';
import { notifyAdmins } from '../utils/notificationService';

export default function SignUp() {
  const navigate = useNavigate();
  const [role, setRole] = useState('student'); // 'student' | 'instructor'
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('form'); // form | check-inbox
  const [createdUser, setCreatedUser] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { addToast } = useToast();

  const startCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !createdUser) return;
    try {
      await sendEmailVerification(createdUser);
      startCooldown();
      addToast('تم إعادة إرسال رابط التفعيل بنجاح!', 'success');
    } catch (err) {
      console.error('Resend error:', err);
      addToast('حدث خطأ أثناء إعادة إرسال الرابط.', 'error');
    }
  };

  const checkAndGenerateUsername = async (inputUsername) => {
    let finalUsername = inputUsername.trim().toLowerCase();
    if (!finalUsername) {
      finalUsername = 'user_' + crypto.randomUUID().split('-')[0];
    } else {
      const usernameQ = query(collection(db, 'users'), where('username', '==', finalUsername));
      const usernameSnap = await getDocs(usernameQ);
      if (!usernameSnap.empty) {
        finalUsername = finalUsername + '_' + crypto.randomUUID().split('-')[0];
      }
    }
    return finalUsername;
  };

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Extract details
      const displayName = user.displayName || 'مستخدم جديد';
      const email = user.email;
      const photoURL = user.photoURL || '';

      // Check if user document already exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        const generatedUsername = await checkAndGenerateUsername('');
        
        let userData = {
          name: displayName,
          username: generatedUsername,
          email,
          photoURL,
          role: role,
          registrationDate: serverTimestamp(),
        };

        if (role === 'instructor') {
          userData.instructorData = {
            status: 'incomplete',  // Needs onboarding
            commissionRate: 0,
            canUploadCourses: false,
            bio: '',
            specialization: ''
          };
        }

        await setDoc(userDocRef, userData);

        await addDoc(collection(db, 'customers'), {
          name: displayName,
          username: generatedUsername,
          email,
          whatsapp: '',
          level: 'beginner',
          status: 'interested',
          amountPaid: 0,
          source: 'google_signup',
          userId: user.uid,
          createdAt: serverTimestamp(),
        });
        await notifyAdmins('مستخدم جديد', 'تم تسجيل مستخدم جديد في المنصة باسم: ' + displayName, 'info', '/admin/users');
      }

      // Successful Google login/signup
      // Check for saved redirect URL
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl, { replace: true });
      } else {
        if (userDocSnap.exists() && userDocSnap.data().role === 'instructor') {
          navigate('/instructor-dashboard', { replace: true });
        } else if (role === 'instructor') {
          navigate('/instructor-onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
      addToast('تم إنشاء/تسجيل الحساب بواسطة Google بنجاح!', 'success');
    } catch (err) {
      console.error(err);
      setError('فشل تسجيل الدخول بواسطة جوجل.');
      addToast('فشل التسجيل باستخدام حساب جوجل.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const finalUsername = await checkAndGenerateUsername(username);

      await sendEmailVerification(user);
      setCreatedUser(user);

      let userData = {
        name,
        username: finalUsername,
        email,
        role: role,
        registrationDate: serverTimestamp(),
      };

      if (role === 'instructor') {
        userData.instructorData = {
          status: 'incomplete',  
          commissionRate: 0,     
          canUploadCourses: false, 
          bio: '',
          specialization: ''
        };
      }

      await setDoc(doc(db, 'users', user.uid), userData);

      await addDoc(collection(db, 'customers'), {
        name,
        username: finalUsername,
        email,
        whatsapp: '',
        level: 'beginner',
        status: 'interested',
        amountPaid: 0,
        source: 'signup',
        userId: user.uid,
        createdAt: serverTimestamp(),
      });

      await notifyAdmins('مستخدم جديد', 'تم تسجيل مستخدم جديد في المنصة باسم: ' + name, 'info', '/admin/users');

      await auth.signOut();
      startCooldown();
      setPhase('check-inbox');
      addToast('تم إنشاء الحساب بنجاح! يرجى الاستمرار للتحقق من بريدك.', 'success');
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('البريد الإلكتروني مستخدم بالفعل');
        addToast('البريد الإلكتروني مستخدم بالفعل', 'error');
      } else if (err.code === 'auth/weak-password') {
        setError('كلمة المرور ضعيفة جداً');
        addToast('كلمة المرور ضعيفة جداً', 'error');
      } else {
        setError('حدث خطأ أثناء إنشاء الحساب. يرجى المحاولة مرة أخرى.');
        addToast('حدث خطأ أثناء إنشاء الحساب. يرجى المحاولة مرة أخرى.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#050505] relative overflow-hidden px-4 transition-colors duration-700 py-12 ${role === 'instructor' ? 'instructor-mode' : ''}`}>
      {/* Ambient Cinematic Glows */}
      <div className={`absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-colors duration-700 ${role === 'instructor' ? 'bg-amber-500/20 dark:bg-amber-600/10' : 'bg-primary-500/20 dark:bg-primary-600/10'}`}></div>
      <div className={`absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[150px] translate-y-1/3 -translate-x-1/4 pointer-events-none transition-colors duration-700 ${role === 'instructor' ? 'bg-orange-500/10 dark:bg-orange-600/10' : 'bg-purple-500/10 dark:bg-purple-600/10'}`}></div>

      <div className={`relative z-10 max-w-md w-full bg-white/80 dark:bg-zinc-900/50 backdrop-blur-2xl rounded-lg shadow-2xl border border-slate-100 dark:border-white/5 p-8 md:p-10 transform transition-shadow duration-500 ${role === 'instructor' ? 'shadow-amber-500/10 dark:shadow-amber-500/5' : 'shadow-primary-500/10 dark:shadow-primary-500/5'}`}>

        {phase === 'form' ? (
          <>
            <div className="text-center mb-8">
              <Link to="/" className={`inline-block text-4xl font-black bg-clip-text text-transparent bg-gradient-to-l mb-3 drop-shadow-sm transition-colors duration-500 ${role === 'instructor' ? 'from-amber-600 to-amber-400 dark:from-amber-400 dark:to-orange-400' : 'from-primary-600 to-primary-400 dark:from-primary-400 dark:to-purple-400'}`}>
                لُبـــــــــاب
              </Link>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">إنشاء حساب جديد</h2>
              <p className="text-slate-500 dark:text-zinc-400 mt-2 font-bold transition-colors">
                {role === 'instructor' ? 'انضم لنخبة المدربين في مدرسة لُباب' : 'ابدأ رحلتك الفنية في الاستوديو'}
              </p>
            </div>

            {/* Role Toggle Switch */}
            <div className="flex bg-slate-100 dark:bg-black/40 p-1 rounded-2xl mb-8 border border-slate-200 dark:border-white/5 relative z-20">
              <div 
                className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white dark:bg-zinc-800 rounded-xl shadow-md transition-transform duration-300 ease-out z-0 border border-slate-200 dark:border-white/5 ${role === 'student' ? 'translate-x-0' : '-translate-x-full'}`}
              ></div>
              <button
                type="button"
                onClick={() => setRole('student')}
                className={`flex-1 flex justify-center items-center gap-2 py-3 text-sm font-bold rounded-xl transition-colors duration-300 z-10 ${role === 'student' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'}`}
              >
                <GraduationCap className="w-4 h-4" /> طالب
              </button>
              <button
                type="button"
                onClick={() => setRole('instructor')}
                className={`flex-1 flex justify-center items-center gap-2 py-3 text-sm font-bold rounded-xl transition-colors duration-300 z-10 ${role === 'instructor' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'}`}
              >
                <ShieldCheck className="w-4 h-4" /> مدرب
              </button>
            </div>

            {error && (
              <div className="mb-6 bg-rose-50/80 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 p-4 rounded-2xl flex items-start gap-3 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-md font-bold leading-relaxed">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignUp} className="space-y-5" dir="rtl">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">الاسم الكامل</label>
                <div className="relative group">
                  <div className={`absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500' : 'group-focus-within:text-primary-500'}`}>
                    <User className={`h-5 w-5 transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500 text-slate-400 dark:text-zinc-500' : 'group-focus-within:text-primary-500 text-slate-400 dark:text-zinc-500'}`} />
                  </div>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`block text-md w-full pr-12 pl-4 py-3 border border-slate-200 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 transition-all duration-300 hover:bg-white dark:hover:bg-white/5 font-medium outline-none focus:ring-2 ${role === 'instructor' ? 'focus:ring-amber-500 focus:border-amber-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
                    placeholder={role === 'instructor' ? "الاسم كما سيظهر للطلاب" : "الاسم الثلاثي"}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">اسم المستخدم (اختياري)</label>
                <div className="relative group">
                  <div className={`absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500' : 'group-focus-within:text-primary-500'}`}>
                    <span className="text-slate-400 dark:text-zinc-500 font-sans font-bold">@</span>
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    className={`block text-md w-full pr-12 pl-4 py-3 border border-slate-200 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 transition-all duration-300 hover:bg-white dark:hover:bg-white/5 font-medium outline-none focus:ring-2 ${role === 'instructor' ? 'focus:ring-amber-500 focus:border-amber-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
                    placeholder="student_24"
                    dir="ltr"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">البريد الإلكتروني</label>
                <div className="relative group">
                  <div className={`absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500' : 'group-focus-within:text-primary-500'}`}>
                    <Mail className={`h-5 w-5 transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500 text-slate-400 dark:text-zinc-500' : 'group-focus-within:text-primary-500 text-slate-400 dark:text-zinc-500'}`} />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`block text-md w-full pr-12 pl-4 py-3 border border-slate-200 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 transition-all duration-300 hover:bg-white dark:hover:bg-white/5 font-medium outline-none focus:ring-2 ${role === 'instructor' ? 'focus:ring-amber-500 focus:border-amber-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">كلمة المرور</label>
                <div className="relative group">
                  <div className={`absolute top-1/2 -translate-y-1/2 right-4 flex items-center pointer-events-none transition-colors z-10 ${role === 'instructor' ? 'group-focus-within:text-amber-500' : 'group-focus-within:text-primary-500'}`}>
                    <Lock className={`h-5 w-5 transition-colors ${role === 'instructor' ? 'group-focus-within:text-amber-500 text-slate-400 dark:text-zinc-500' : 'group-focus-within:text-primary-500 text-slate-400 dark:text-zinc-500'}`} />
                  </div>
                  <PasswordInput
                    id="signup-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onValidityChange={setIsPasswordValid}
                    className="pl-4 pr-12 py-3 text-md border border-slate-200 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-black/20"
                    placeholder="اختر كلمة مرور قوية"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || !isPasswordValid}
                  className={`w-full flex text-lg font-semibold justify-center items-center py-4 px-4 border border-transparent rounded-2xl shadow-lg focus:outline-none transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 text-white ${role === 'instructor' ? 'shadow-amber-500/20 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400' : 'shadow-primary-500/20 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400'}`}
                >
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'إنشاء الحساب'}
                </button>
              </div>

              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
                <span className="shrink-0 px-4 text-slate-400 dark:text-zinc-500 text-sm font-medium">أو</span>
                <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleAuth}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 border border-slate-200 dark:border-white/10 rounded-2xl bg-white dark:bg-zinc-800 text-slate-700 dark:text-white font-bold hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors duration-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                متابعة باستخدام Google
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200/50 dark:border-white/5 text-center space-y-5">
              <p className="text-lg font-semibold text-slate-600 dark:text-zinc-400">
                لديك حساب بالفعل؟{' '}
                <Link to="/login" className={`font-extrabold text-xl hover:opacity-80 transition-opacity ${role === 'instructor' ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-400'}`}>
                  تسجيل الدخول
                </Link>
              </p>

              <Link to="/" className={`inline-flex items-center gap-2 text-md font-semibold text-slate-500 dark:text-zinc-500 transition-colors group ${role === 'instructor' ? 'hover:text-amber-600 dark:hover:text-amber-400' : 'hover:text-primary-600 dark:hover:text-primary-400'}`}>
                <ArrowRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                العودة إلى الرئيسية
              </Link>
            </div>
          </>
        ) : (
          /* ── PHASE: CHECK INBOX ── */
          <div className="text-center py-6 space-y-6">
            {/* Animated Mail Icon */}
            <div className="relative w-24 h-24 mx-auto">
              <div className={`absolute inset-0 rounded-full animate-ping opacity-30 ${role === 'instructor' ? 'bg-amber-500/20' : 'bg-primary-500/20'}`}></div>
              <div className={`relative w-24 h-24 rounded-full flex items-center justify-center border-2 shadow-xl ${role === 'instructor' ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 shadow-amber-500/20' : 'bg-primary-50 dark:bg-primary-500/10 border-primary-200 dark:border-primary-500/30 shadow-primary-500/20'}`}>
                <Mail className={`w-10 h-10 ${role === 'instructor' ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-400'}`} />
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">فعّل حسابك</h2>
              <p className="text-slate-500 dark:text-zinc-400 font-bold text-lg leading-relaxed max-w-sm mx-auto">
                تم إرسال رابط التفعيل إلى<br />
                <span className={`font-black ${role === 'instructor' ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-400'}`}>{email}</span>
              </p>
            </div>

            <div className="flex items-center gap-2 justify-center text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-2xl py-3 px-5">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="font-bold text-md">تحقق من صندوق الوارد والبريد المزعج</span>
            </div>

            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              {resendCooldown > 0 ? `إعادة الإرسال بعد ${resendCooldown} ثانية` : 'إعادة إرسال الرابط'}
            </button>

            <div className="pt-4">
              <Link to="/login" className={`inline-flex items-center gap-2 text-lg font-bold transition-colors group ${role === 'instructor' ? 'text-amber-600 dark:text-amber-400 hover:text-amber-500' : 'text-primary-600 dark:text-primary-400 hover:text-primary-500'}`}>
                <ArrowRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                الانتقال لتسجيل الدخول
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
